import { supabase } from '../config/supabase';

export interface BackupData {
  users?: any[];
  properties?: any[];
  monthly_stats?: any[];
  timestamp?: string;
  version?: string;
}

export interface RestoreResult {
  success: boolean;
  usersRestored: number;
  propertiesRestored: number;
  monthlyStatsRestored: number;
  errors: string[];
}

/**
 * Valida que el archivo de backup tenga el formato correcto
 */
export function validateBackupFile(data: any): { valid: boolean; error?: string } {
  if (!data || typeof data !== 'object') {
    return { valid: false, error: 'El archivo no tiene un formato válido' };
  }

  // Verificar que tenga al menos una tabla
  if (!data.users && !data.properties && !data.monthly_stats) {
    return { valid: false, error: 'El archivo no contiene datos de backup' };
  }

  // Verificar que las tablas sean arrays
  if (data.users && !Array.isArray(data.users)) {
    return { valid: false, error: 'Los datos de usuarios no son válidos' };
  }
  if (data.properties && !Array.isArray(data.properties)) {
    return { valid: false, error: 'Los datos de propiedades no son válidos' };
  }
  if (data.monthly_stats && !Array.isArray(data.monthly_stats)) {
    return { valid: false, error: 'Los datos mensuales no son válidos' };
  }

  return { valid: true };
}

/**
 * Obtiene un resumen del contenido del backup
 */
export function getBackupSummary(data: BackupData) {
  return {
    users: data.users?.length || 0,
    properties: data.properties?.length || 0,
    monthlyStats: data.monthly_stats?.length || 0,
    timestamp: data.timestamp || 'Desconocido',
    version: data.version || 'Desconocido',
  };
}

/**
 * Restaura usuarios desde el backup
 */
async function restoreUsers(users: any[]): Promise<{ success: number; errors: string[] }> {
  const errors: string[] = [];
  let success = 0;

  try {
    // Borrar todos los usuarios existentes (excepto admin)
    const { error: deleteError } = await supabase
      .from('users')
      .delete()
      .neq('role', 'admin');

    if (deleteError) {
      errors.push(`Error borrando usuarios: ${deleteError.message}`);
      return { success: 0, errors };
    }

    // Insertar usuarios del backup
    for (const user of users) {
      const { error } = await supabase
        .from('users')
        .upsert({
          id: user.id,
          username: user.username,
          password: user.password,
          role: user.role,
          property_id: user.property_id,
          created_at: user.created_at,
        });

      if (error) {
        errors.push(`Error restaurando usuario ${user.username}: ${error.message}`);
      } else {
        success++;
      }
    }
  } catch (error: any) {
    errors.push(`Error general restaurando usuarios: ${error.message}`);
  }

  return { success, errors };
}

/**
 * Restaura propiedades desde el backup
 */
async function restoreProperties(properties: any[]): Promise<{ success: number; errors: string[] }> {
  const errors: string[] = [];
  let success = 0;

  try {
    // Borrar todas las propiedades existentes
    const { error: deleteError } = await supabase
      .from('properties')
      .delete()
      .neq('id', '00000000-0000-0000-0000-000000000000'); // Borrar todas

    if (deleteError) {
      errors.push(`Error borrando propiedades: ${deleteError.message}`);
      return { success: 0, errors };
    }

    // Insertar propiedades del backup
    for (const property of properties) {
      const { error } = await supabase
        .from('properties')
        .upsert({
          id: property.id,
          name: property.name,
          location: property.location,
          owner_id: property.owner_id,
          stats: property.stats,
          created_at: property.created_at,
        });

      if (error) {
        errors.push(`Error restaurando propiedad ${property.name}: ${error.message}`);
      } else {
        success++;
      }
    }
  } catch (error: any) {
    errors.push(`Error general restaurando propiedades: ${error.message}`);
  }

  return { success, errors };
}

/**
 * Restaura datos mensuales desde el backup
 */
async function restoreMonthlyStats(monthlyStats: any[]): Promise<{ success: number; errors: string[] }> {
  const errors: string[] = [];
  let success = 0;

  try {
    // Borrar todos los datos mensuales
    const { error: deleteError } = await supabase
      .from('monthly_stats')
      .delete()
      .neq('id', '00000000-0000-0000-0000-000000000000');

    if (deleteError) {
      errors.push(`Error borrando datos mensuales: ${deleteError.message}`);
      return { success, errors };
    }

    // Insertar datos del backup
    for (const stat of monthlyStats) {
      const { error } = await supabase
        .from('monthly_stats')
        .upsert({
          id: stat.id,
          property_id: stat.property_id,
          year: stat.year,
          month: stat.month,
          revenue: stat.revenue,
          cleaning_costs: stat.cleaning_costs,
          inmohost_costs: stat.inmohost_costs,
          profit: stat.profit,
          occupancy_rate: stat.occupancy_rate,
          reservations: stat.reservations,
          average_night_price: stat.average_night_price,
          average_stay_duration: stat.average_stay_duration,
          created_at: stat.created_at,
        });

      if (error) {
        errors.push(`Error restaurando dato mensual: ${error.message}`);
      } else {
        success++;
      }
    }
  } catch (error: any) {
    errors.push(`Error general restaurando datos mensuales: ${error.message}`);
  }

  return { success, errors };
}

/**
 * Restaura TODOS los datos desde un backup (restauración completa)
 */
export async function restoreFromBackup(backupData: BackupData): Promise<RestoreResult> {
  const result: RestoreResult = {
    success: true,
    usersRestored: 0,
    propertiesRestored: 0,
    monthlyStatsRestored: 0,
    errors: [],
  };

  try {
    // Restaurar usuarios
    if (backupData.users && backupData.users.length > 0) {
      const userResult = await restoreUsers(backupData.users);
      result.usersRestored = userResult.success;
      result.errors.push(...userResult.errors);
    }

    // Restaurar propiedades
    if (backupData.properties && backupData.properties.length > 0) {
      const propertyResult = await restoreProperties(backupData.properties);
      result.propertiesRestored = propertyResult.success;
      result.errors.push(...propertyResult.errors);
    }

    // Restaurar datos mensuales
    if (backupData.monthly_stats && backupData.monthly_stats.length > 0) {
      const statsResult = await restoreMonthlyStats(backupData.monthly_stats);
      result.monthlyStatsRestored = statsResult.success;
      result.errors.push(...statsResult.errors);
    }

    result.success = result.errors.length === 0;
  } catch (error: any) {
    result.success = false;
    result.errors.push(`Error general: ${error.message}`);
  }

  return result;
}
